// IndexedDB service for storing and processing payroll data

// Database configuration
const DB_NAME = "PayrollDB"
const DB_VERSION = 1
const STORE_NAME = "payrollData"

// Setup IndexedDB
export const setupIndexedDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = (event) => {
      reject(new Error("Failed to open IndexedDB"))
    }

    request.onsuccess = (event) => {
      resolve(request.result)
    }

    request.onupgradeneeded = (event) => {
      const db = request.result

      // Create object store for payroll data
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" })

        // Create indexes for faster queries
        store.createIndex("employeeNo", "employeeNo", { unique: false })
        store.createIndex("processed", "processed", { unique: false })
        store.createIndex("payrollId", "payrollId", { unique: false })
      }
    }
  })
}

// Store data in IndexedDB
export const storeDataInIndexedDB = (data) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME)

    request.onerror = (event) => {
      reject(new Error("Failed to open IndexedDB for storing data"))
    }

    request.onsuccess = async (event) => {
      const db = request.result
      const transaction = db.transaction(STORE_NAME, "readwrite")
      const store = transaction.objectStore(STORE_NAME)

      // Clear existing data
      await clearStore(store)

      // Add new data
      let count = 0
      for (const item of data) {
        // Mark as not processed
        const record = { ...item, pensionProcessed: 0 }
        const addRequest = store.add(record)

        addRequest.onerror = (event) => {
          console.error("Error adding record:", event)
        }

        count++
      }

      transaction.oncomplete = () => {
        resolve(count)
      }

      transaction.onerror = (event) => {
        reject(new Error("Transaction failed when storing data"))
      }
    }
  })
}

// Clear the object store
const clearStore = (store) => {
  return new Promise((resolve, reject) => {
    const clearRequest = store.clear()

    clearRequest.onsuccess = () => {
      resolve()
    }

    clearRequest.onerror = (event) => {
      reject(new Error("Failed to clear store"))
    }
  })
}

// Process data from IndexedDB and send to API
export const processDataFromIndexedDB = (apiEndpoint, progressCallback, statusCallback) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME)

    request.onerror = (event) => {
      reject(new Error("Failed to open IndexedDB for processing"))
    }

    request.onsuccess = async (event) => {
      const db = request.result
      const transaction = db.transaction(STORE_NAME, "readonly")
      const store = transaction.objectStore(STORE_NAME)

      // Get all records
      const getAllRequest = store.getAll()

      getAllRequest.onsuccess = async () => {
        const records = getAllRequest.result
        const total = records.length

        if (total === 0) {
          statusCallback("No records to process")
          resolve()
          return
        }

        statusCallback(`Processing ${total} records...`)
        progressCallback(0, total)

        // Process records one by one
        let processed = 0
        let success = 0
        let failed = 0

        for (const record of records) {
          try {
            // Prepare pension data from the record
            const pensionData = {
              id: 0, // Will be assigned by the server
              employeeNo: record.employeeNo,
              project: record.project || "",
              amount: record.amount,
              accountingString: record.accountingString || "Accounting String",
              employeeName: record.employeeName,
              payrollId: record.payrollId,
              benefit: record.benefit,
              obj: "pension", // Mark as pension data
              period: record.period,
              processed: 0,
              location: record.location || "",
              grade: record.grade || "",
              grp: record.grp,
              timeStamp: new Date().toISOString(),
              includeInApportionment: 1,
              sysUser: record.sysUser || "system",
            }

            // Send to API
            await fetch(apiEndpoint, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(pensionData),
            })

            // Mark as processed in IndexedDB
            await markAsProcessed(record.id)

            success++
          } catch (error) {
            console.error(`Error processing record ${record.id}:`, error)
            failed++
          }

          processed++
          progressCallback(processed, total)
          statusCallback(`Processed ${processed}/${total} records (${success} success, ${failed} failed)`)

          // Small delay to prevent overwhelming the server
          await new Promise((r) => setTimeout(r, 50))
        }

        statusCallback(`Completed processing ${total} records (${success} success, ${failed} failed)`)
        resolve()
      }

      getAllRequest.onerror = (event) => {
        reject(new Error("Failed to get records from IndexedDB"))
      }
    }
  })
}

// Mark a record as processed
const markAsProcessed = (id) => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME)

    request.onsuccess = (event) => {
      const db = request.result
      const transaction = db.transaction(STORE_NAME, "readwrite")
      const store = transaction.objectStore(STORE_NAME)

      // Get the record
      const getRequest = store.get(id)

      getRequest.onsuccess = () => {
        const record = getRequest.result
        if (record) {
          // Update the record
          record.pensionProcessed = 1

          // Put the updated record back
          const updateRequest = store.put(record)

          updateRequest.onsuccess = () => {
            resolve()
          }

          updateRequest.onerror = (event) => {
            reject(new Error(`Failed to update record ${id}`))
          }
        } else {
          reject(new Error(`Record ${id} not found`))
        }
      }

      getRequest.onerror = (event) => {
        reject(new Error(`Failed to get record ${id}`))
      }
    }

    request.onerror = (event) => {
      reject(new Error("Failed to open IndexedDB for marking as processed"))
    }
  })
}
